import { useCompactNumberFormatter } from '@shared/hooks/useCompactNumberFormatter';
import type { AccommodationsConsoleState, JoyHotelAccommodationListingDTO, RawJoyHotelPricingInfo, StandardHotelPricingValues } from '@shared/types/Accommodations.types';
import type { StaysSearchTerms } from '@apps/stays/types';
import type { PriceSource } from '@shared/constants/accommodations';
import { isValidLatLngLocation } from '@shared/utils/locationValidation';
import type { AccommodationsConsolePropertiesV2Query } from '../../../apps/stays/AccommodationsConsole/AccommodationsConsole.graphql.generated';
import { extractHotelRedirects } from '@shared/utils/extractHotelRedirects';
import { validateTravelOfferDiag } from '@shared/types/TravelOfferDiag.validation';

type CancellationPolicyLike = Readonly<{
  estimatedCancellationAmount?: { amount: { valueInMinorUnits: number } } | null;
}>;

export const hasAnyCancellationPolicyFree = (policies: Maybe<readonly CancellationPolicyLike[]>): boolean =>
  policies?.some(policy => !policy.estimatedCancellationAmount || policy.estimatedCancellationAmount.amount.valueInMinorUnits === 0) ?? false;

export type AccommodationConsoleQueryProperty = AccommodationsConsolePropertiesV2Query['searchExtendedAccommodationProperties']['properties'][number];

export type ValidAccommodationConsoleQueryProperty = Merge<
  AccommodationConsoleQueryProperty,
  { readonly location: { readonly latitude: number; readonly longitude: number }; readonly address: NonNullable<AccommodationConsoleQueryProperty['address']> }
>;

export const isValidProperty = (property: AccommodationConsoleQueryProperty | null): property is ValidAccommodationConsoleQueryProperty => {
  return !!property && isValidLatLngLocation(property.location) && !!property.address;
};

/**
 * @param priceSource - Which fetch path produced these properties' prices. Required, so every caller
 *   states it. The same query serves both a cached search and a realtime one. Only the caller knows
 *   which `fetchStrategy` it sent.
 */
export const transformPropertiesToListings = (
  properties: ValidAccommodationConsoleQueryProperty[],
  mapHotelRoomPrice: (pricingInfo: RawJoyHotelPricingInfo) => StandardHotelPricingValues | null,
  priceSource: PriceSource,
): JoyHotelAccommodationListingDTO[] => {
  return properties
    .sort((a, b) => b.score - a.score)
    .map(listing => {
      // Null when the property has no rate for these dates, or the payload carries no per-night amount.
      const pricingInfoValue = listing.pricingInfo ? mapHotelRoomPrice(listing.pricingInfo) : null;

      return {
        userNote: null,
        id: listing.id,
        description: null,
        isFavorite: false,
        name: listing.name,
        searchScore: listing.score,
        isSponsored: listing.isSponsored ?? false,
        joyPlaceId: listing.joyPlaceId,
        type: 'standardHotel' as const,
        facilities: listing.facilities,
        address: {
          city: listing.address.city,
          street: listing.address.street,
          country: listing.address.country,
        },
        otaProvider: listing.otaProvider,
        accommodationCategory: listing.accommodationCategory,
        chain: listing.chain ?? null,
        location: {
          lat: listing.location.latitude,
          lng: listing.location.longitude,
        },
        starRating: listing.starRating,
        tags: listing.tags,
        userRating: listing.userRating
          ? {
              ratingValue: listing.userRating.ratingValue,
              ratingCount: listing.userRating.ratingCount,
            }
          : null,
        mediaAssets: {
          isLoading: false,
          photos: listing.photos,
        },
        pricingInfo: {
          isLoading: false,
          values: pricingInfoValue,
          // Only a price that landed has a source; otherwise telemetry names a fetch nobody saw.
          source: pricingInfoValue ? priceSource : undefined,
        },
        diag: listing.pricingInfo?._diag ? validateTravelOfferDiag(listing.pricingInfo._diag) : null,
        redirectMechanisms: listing.redirectMechanisms || undefined,
        isSoldOutForSelectedDates: !!extractHotelRedirects(listing.redirectMechanisms)?.hotelSearchPageUrl,
        hasFreeCancellation: hasAnyCancellationPolicyFree(listing.pricingInfo?.cancellationPolicies),
        // Absent unless the cash-fund variant asked for it; the fund surfaces read it, nothing else.
        walletSpend: listing.walletSpend ?? null,
      };
    });
};

/**
 * Hook for formatting review counts in a compact way (e.g., 1000 -> 1K) using the current locale.
 */
export const useReviewCountFormatter = () => {
  return useCompactNumberFormatter().formatCompactNumber;
};

/**
 * Formats a user rating number to one decimal place.
 *
 * eg. 8.5 -> "8.5", 9 -> "9.0"
 */
export const formatUserRatingNumber = (rating: number): string => {
  return rating.toFixed(1);
};

export type ParamsUpdater = (key: string, value: string | null) => void;

export const createQueryParamsUpdater =
  (params: URLSearchParams): ParamsUpdater =>
  (key, value) => {
    if (value === null) {
      params.delete(key);
    } else {
      params.set(key, value);
    }
  };

export const getSearchFormInitialValue = (state: AccommodationsConsoleState): Partial<StaysSearchTerms> => {
  return {
    destination: state.searchLocation,
    lat: state.circularRegionSearch?.latitude,
    lng: state.circularRegionSearch?.longitude,
    startDate: {
      year: parseInt(state.searchDates.checkin.split('-')[0]),
      month: parseInt(state.searchDates.checkin.split('-')[1]),
      day: parseInt(state.searchDates.checkin.split('-')[2]),
    },
    endDate: {
      year: parseInt(state.searchDates.checkout.split('-')[0]),
      month: parseInt(state.searchDates.checkout.split('-')[1]),
      day: parseInt(state.searchDates.checkout.split('-')[2]),
    },
    rooms:
      state.occupancies?.occupancies?.map(occ => ({
        adults: occ.adults,
        childrenAges: (occ.childrenAges || []).filter((age): age is number => age !== null),
      })) || [],
  };
};
